import { useCallback, useEffect, useRef, useState } from 'react';

export interface IdleTimerConfig {
    timeout: number // in milliseconds
    warningTime: number // in milliseconds before timeout to show warning
    onIdle: () => void
    onActive: () => void
    onWarning: () => void
    debounceDelay?: number // delay between activity events
    events?: string[] // events to listen for activity
    element?: Document | Element // element to attach listeners to
    startOnMount?: boolean // whether to start the timer on mount
}

export interface IdleTimerHook {
    isIdle: boolean
    isWarning: boolean
    remainingTime: number
    totalTime: number
    start: () => void
    stop: () => void
    reset: () => void
    pause: () => void
    resume: () => void
    getRemainingTime: () => number
}

const DEFAULT_EVENTS = [
  'mousedown',
  'mousemove',
  'keypress',
  'scroll',
  'touchstart',
  'click',
  'wheel'
];

export const useIdleTimer = (config: IdleTimerConfig): IdleTimerHook => {
  const {
    timeout,
    warningTime,
    onIdle,
    onActive,
    onWarning,
    debounceDelay = 500,
    events = DEFAULT_EVENTS,
    element = document,
    startOnMount = true
  } = config;

  const [isIdle, setIsIdle] = useState<boolean>(false);
  const [isWarning, setIsWarning] = useState<boolean>(false);
  const [remainingTime, setRemainingTime] = useState<number>(timeout);

  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const warningId = useRef<NodeJS.Timeout | null>(null);
  const intervalId = useRef<NodeJS.Timeout | null>(null);
  const debounceId = useRef<NodeJS.Timeout | null>(null);
  const lastActivity = useRef<number>(Date.now());
  const isPaused = useRef<boolean>(false);

  const clearAllTimers = useCallback(() => {
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
      timeoutId.current = null;
    }
    if (warningId.current) {
      clearTimeout(warningId.current);
      warningId.current = null;
    }
    if (intervalId.current) {
      clearInterval(intervalId.current);
      intervalId.current = null;
    }
    if (debounceId.current) {
      clearTimeout(debounceId.current);
      debounceId.current = null;
    }
  }, []);

  const updateRemainingTime = useCallback(() => {
    if (isPaused.current) return;

    const now = Date.now();
    const elapsed = now - lastActivity.current;
    const remaining = Math.max(0, timeout - elapsed);
    setRemainingTime(remaining);

    if (remaining === 0 && !isIdle) {
      setIsIdle(true);
      setIsWarning(false);
      onIdle();
    }
  }, [timeout, isIdle, onIdle]);

  const startTimers = useCallback(() => {
    clearAllTimers();
    lastActivity.current = Date.now();
    setRemainingTime(timeout);
    setIsIdle(false);
    setIsWarning(false);

    // Start interval to update remaining time
    intervalId.current = setInterval(updateRemainingTime, 1000);

    // Set warning timer
    if (warningTime > 0 && warningTime < timeout) {
      warningId.current = setTimeout(() => {
        if (!isPaused.current && !isIdle) {
          setIsWarning(true);
          onWarning();
        }
      }, timeout - warningTime);
    }

    // Set idle timer
    timeoutId.current = setTimeout(() => {
      if (!isPaused.current) {
        setIsIdle(true);
        setIsWarning(false);
        onIdle();
      }
    }, timeout);
  }, [timeout, warningTime, onIdle, onWarning, isIdle, clearAllTimers, updateRemainingTime]);

  const handleActivity = useCallback(() => {
    if (isPaused.current) return;

    // Debounce activity events
    if (debounceId.current) {
      clearTimeout(debounceId.current);
    }

    debounceId.current = setTimeout(() => {
      const wasIdle = isIdle;
      const wasWarning = isWarning;

      if (wasIdle || wasWarning) {
        onActive();
      }

      startTimers();
    }, debounceDelay);
  }, [isIdle, isWarning, onActive, startTimers, debounceDelay]);

  const start = useCallback(() => {
    isPaused.current = false;
    startTimers();
  }, [startTimers]);

  const stop = useCallback(() => {
    isPaused.current = true;
    clearAllTimers();
    setIsIdle(false);
    setIsWarning(false);
    setRemainingTime(timeout);
  }, [clearAllTimers, timeout]);

  const reset = useCallback(() => {
    if (!isPaused.current) {
      startTimers();
    }
  }, [startTimers]);

  const pause = useCallback(() => {
    isPaused.current = true;
    clearAllTimers();
  }, [clearAllTimers]);

  const resume = useCallback(() => {
    isPaused.current = false;
    startTimers();
  }, [startTimers]);

  const getRemainingTime = useCallback(() => {
    if (isPaused.current) return timeout;
    const now = Date.now();
    const elapsed = now - lastActivity.current;
    return Math.max(0, timeout - elapsed);
  }, [timeout]);

  // Setup event listeners
  useEffect(() => {
    const targetElement = element as Document | Element;

    events.forEach(event => {
      targetElement.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        targetElement.removeEventListener(event, handleActivity);
      });
    };
  }, [events, element, handleActivity]);

  // Start timer on mount if configured
  useEffect(() => {
    if (startOnMount) {
      start();
    }

    return () => {
      stop();
    };
  }, [startOnMount, start, stop]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);

  return {
    isIdle,
    isWarning,
    remainingTime,
    totalTime: timeout,
    start,
    stop,
    reset,
    pause,
    resume,
    getRemainingTime
  };
};
